import React, { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import Review from './_review/component';
import { Button, Rating } from 'audamatic-ui';
import SectionHeader from '../../base_section-header/component';
import Icon from '../../base_svg-icon/component';
import Restapi from '@utils/api';
import { Row, Col } from '../../util_grid/grid';
import { miniMustache, getDevice, appStorage } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';

import './styles/styles.scss';

/**
 * Reviews component
 */
class Reviews extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loggedIn: false,
      rawReviews: [],
      reviews: [],
      writeModus: 0, // 0, 1, 2
      myReview: {
        title: '',
        description: '',
        rating: 0,
        isFormValid: false,
      },
      moreInfoOpen: false,
      rating: 0,
      page: 0,
      fetchingReviews: true,
    };

    const { isMobile } = getDevice();

    this.reviewsPerPage = isMobile ? 2 : 3; // the amount of revieuws to request per bulk
    this.titleMinLength = 0; // The minimum length for the review title
    this.titleMaxLength = 100; // The maximum length for the review title
    this.descriptionMinLength = 0; // The minimum length for the review content
    this.descriptionMaxLength = 4000; // The maximum length for the review content
    this._pendingreviews = []; // the reviews that are stored locally

    this.restapi = new Restapi();

    this.description = createRef();
    this.title = createRef();
    this.form = createRef();
    this.preview = createRef();
  }

  componentDidMount() {
    // get the local stored reviews to the state.
    this.pendingReviewsToState();
    this.fetchReviews();
    this.checkLoggedIn();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sku !== this.props.sku) {
      this.setState({ reviews: [], page: 0 }, () => {
        this.pendingReviewsToState();
        this.fetchReviews();
      });
    }
  }

  /*
   * this.extendReviews needs to be called as callback of setState
   * This way it is triggered after the state is updated and initial reviews on pageload show
   */

  fetchReviews = () => {
    this.restapi.getReviews({ sku: this.sku })
      .then((reviews = []) => {
        this.cleanUpLocalStoredreviews({ reviewsApproved: reviews.elements });
        this.setState({ rawReviews: reviews.elements }, this.extendReviews);
        this.props.setReviewCount(reviews.elements.length);
        return;
      })
      .then(this.getAnchor);
  };

  /**
   * Checks if the url has a '#reviews' in the url. Once it has, it will scroll to this part of the PDP
   */
  getAnchor() {
    const anchor = location.hash.substr(1);
    if (anchor === 'reviews') {
      const elm = document.getElementById('reviewsRef');
      elm.scrollIntoView();
    }
  }

  cleanUpLocalStoredreviews({ reviewsApproved }) {
    const approvedIds = [];
    reviewsApproved.forEach(review => approvedIds.push(review.title));

    this._pendingreviews.forEach((review) => {
      if (approvedIds.includes(review.id)) {
        // remove from storage
        this.removeReviewFromStorage(review.id);

        const filteredReviews = this.state.reviews.filter(item => item.id !== review.id);
        this.setState({ reviews: filteredReviews });
      }
    });
  }

  checkLoggedIn() {
    this.store.loadBasket({ refresh: false, origin: 'store:reviews' })
      .then(basket => this.setState({ loggedIn: !!basket.user }));
  }

  get name() {
    if (!this.state.loggedIn) {
      return {};
    }
    const { firstName: authorFirstName, lastName: authorLastName } = this.store.getBasket().user || {};
    return ({ authorFirstName, authorLastName });
  }

  /**
   * The average rating of this product
   * @returns {*}
   */
  get rating() {
    const { rating } = this.props;
    return rating ? parseInt(rating, 10) : 0;
  }

  get store() {
    return this.props.store || {};
  }

  get sku() {
    return this.props.sku || this.store.getDefaultSku();
  }

  /**
   * The name of the book/ product
   *
   * @returns {String|string}
   */
  get productName() {
    return this.props.productName || '';
  }

  get populateMyReviewObject() {
    return {
      authorFirstName: this.name.authorFirstName,
      authorLastName: this.name.authorLastName,
      creationDate: new Date(),
      rating: this.state.myReview.rating,
      title: this.state.myReview.title,
      content: this.state.myReview.description,
      preview: true,
    };
  }

  /**
   * Return a Date object
   * based on date comming from back end
   *
   * @param myDate
   * @returns {Date}
   */
  getDate(myDate) {
    // if myDate is already instance of date return that
    if (myDate instanceof Date) {
      return myDate;
    }

    /*
     * it is probably a string of this format
     * Thu Dec 06 10:56:19 CET 2018
     * const [Thu, Dec, 06, 10:56:19, CET, 2018] = myDate
     */
    const dateChunks = myDate.split(' ');
    const month = dateChunks[1];
    const day = dateChunks[2];
    const year = dateChunks[5];
    return new Date(`${day} ${month} ${year}`);
  }

  setReview = () => {
    const { title, description: content, rating } = this.state.myReview;
    const { authorFirstName, authorLastName } = this.name;
    this.restapi.setReview({ sku: this.sku, reviewObject: {
      title,
      content,
      rating,
    },
    })
      .then((response) => {
        // set the new review to the reviews list
        const id = response.title;

        // Convert current date into EEE MMM dd HH:mm:ss zzz yyyy format that the reviews use
        const timeArr = new Date().toString()
          .split('+')[0].split(' ');
        const timeStamp = timeArr.slice(0, 3).concat(timeArr.slice(4), timeArr[3])
          .join(' ');

        const newReview = {
          id,
          title,
          authorFirstName,
          authorLastName,
          creationDate: timeStamp,
          rating,
          content,
          status: 'new',
          sku: this.sku,
        };
        const reviews = [newReview, ...this.state.reviews];

        // reset the values
        this.savePendingReview(newReview);

        this.setState({
          reviews,
          writeModus: 0,
          myReview: { title: '', description: '' },
        });
      });
  };

  /**
   * Get the actual reviews based on the reviewlist
   * Get them by page
   */
  extendReviews = () => {
    const { rawReviews, reviews, page, fetchingReviews } = this.state;
    if (fetchingReviews === false) {
      this.setState({ fetchingReviews: true });
    }

    const start = (page === 0) ? 0 : (page * this.reviewsPerPage);
    const end = start + this.reviewsPerPage;
    const promises = [];

    rawReviews
      .slice(start, end)
      .forEach((review) => {
        const request = this.restapi.getReview({ sku: this.sku, id: review.title });
        promises.push(request);
      });

    Promise.all(promises).then((newReviews) => {
      this.setState({
        page: page + 1,
        reviews: [...reviews, ...newReviews],
        fetchingReviews: false,
      });
    });
  };

  openLoginModal = (e) => {
    e?.preventDefault?.();

    import('../../sec_login-register-user/component')
      .then((component) => {
        const Login = component.default;
        this.store.modal.openModal('popup', '', {
          component: <Login modal={true} store={this.store} goNext={this.loggedInByModal} />,
        });
      });
  };

  loggedInByModal = () => {
    this.setState({
      loggedIn: !!this.store.getBasket().user,
      writeModus: 1,
    });
    this.store.modal.closeModal();
  };

  /**
   * Save it local
   *
   * @param reviewId
   */
  savePendingReview(newReview) {
    const pendingreviewsString = appStorage.getItem('pendingreviews') || '[]';
    const pendingreviews = JSON.parse(pendingreviewsString);
    pendingreviews.push(newReview);
    appStorage.setItem('pendingreviews', JSON.stringify(pendingreviews));
    this.pendingReviewsToState();
  }

  /**
   * Get the local saved reviews to the reviews
   */
  pendingReviewsToState() {
    const pendingreviewsString = appStorage.getItem('pendingreviews') || '[]';
    const pendingreviews = JSON.parse(pendingreviewsString);

    // remove 'old' reviews
    const reviewsToStay = pendingreviews.filter((item) => {
      const creationDate = new Date(item.creationDate);
      const _commpareDate = new Date();
      _commpareDate.setDate(_commpareDate.getDate() - 7); // reviews that are over  7 days old
      return (creationDate > _commpareDate);
    });

    // if there are items to remove
    if (reviewsToStay.length !== pendingreviews.length) {
      appStorage.setItem('pendingreviews', JSON.stringify(reviewsToStay));
    }

    // newest reviews on top
    this._pendingreviews = reviewsToStay.filter(item => item.sku === this.sku).reverse();

    const reviews = [...this._pendingreviews, ...this.state.reviews];
    this.setState({ reviews });
  }

  /**
   * Remove local stored reviews by id
   * @param id
   */
  removeReviewFromStorage = (id) => {
    const pendingreviewsString = appStorage.getItem('pendingreviews') || '[]';
    const pendingreviews = JSON.parse(pendingreviewsString);
    appStorage.setItem('pendingreviews', JSON.stringify(pendingreviews.filter(item => item.id !== id)));
  };

  startReviewing = (e) => {
    e && e.preventDefault();
    if (this.state.loggedIn) {
      this.gotoStep(1)();
    } else {
      this.openLoginModal();
    }
  };

  gotoStep = (step) => {
    return (e) => {
      e && e.preventDefault();
      this.setState({ writeModus: step });
    };
  };

  handleReviewChange = (reviewAttribute) => {
    return (e) => {
      e.persist();
      e.preventDefault();
      this.setState(
        prevState => ({
          myReview: { ...prevState.myReview, [reviewAttribute]: e.target.value },
        }),
        this.validateFields
      );
    };
  };

  customError(field) {
    const transMap = {
      title: t('comp.reviews.input.title.validation.length') || ' ',
      description: t('comp.reviews.input.description.validation.length') || ' ',
    };

    return transMap[field];
  }

  validateFields() {
    const { description, title } = this.state.myReview;

    const isTitleValid = (title.length >= this.titleMinLength &&
                         title.length <= this.titleMaxLength);

    const isDescriptionValid = (description.length >= this.descriptionMinLength &&
                               description.length <= this.descriptionMaxLength);

    !isTitleValid ?
      this.title.current.setCustomValidity(this.customError('title')) :
      this.title.current.setCustomValidity('');

    !isDescriptionValid ?
      this.description.current.setCustomValidity(this.customError('description')) :
      this.description.current.setCustomValidity('');

    const eitherFieldHasValue = (!!title.length || !!description.length);
    const isFormValid = (isDescriptionValid && isTitleValid) && eitherFieldHasValue;

    this.setState(prevState => ({
      myReview: { ...prevState.myReview, isFormValid },
    }));
  }

  /**
   * Handles the submit event on the form and submits
   * the formData values if the form is valid
   *
   * @param {Event} e
   */
  handleSubmit = (e) => {
    e.preventDefault();
    if (this.validateForm()) {
      this.gotoStep(2)();
      this.preview.current.scrollIntoView();
    }
    this.form.current.classList.add('should-validate');
  };

  /**
   * Returns true or false based on the form checkValidity method
   */
  validateForm = () =>  {
    this.validateFields();
    return this.form.current.checkValidity();
  };

  toggleTips = () => {
    this.setState(prevState => ({
      moreInfoOpen: !prevState.moreInfoOpen,
    }));
  };

  handleReviewChangeRating = (rating) => {
    this.setState(prevState => ({ myReview: { ...prevState.myReview, rating } }));
    this.startReviewing();
  };

  get renderReviewPreview()  {
    const { preview, status = '', title } = this.populateMyReviewObject;
    const pending = status.toLowerCase().includes('new') || false;
    const rejected = status.toLowerCase().includes('rejected') || false;
    const remark = (preview || pending || rejected) ? t('comp.reviews.add-rating.example-title') : null;

    return (
      <Fragment key={title}>
        {remark && (
          <div className="review-remark">{remark}</div>
        )}
        <Review review={this.populateMyReviewObject}/>
        {preview && (
          <div className="reviews-button-block preview">
            <Button variant="secondary" size="large" onClick={this.gotoStep(1)} data-test-id="button-review-change">
              <Text>comp.reviews.button.change</Text>
            </Button>
            <Button variant="primary" size="large" onClick={this.setReview} data-test-id="button-review-place">
              <Text>comp.reviews.button.place</Text>
            </Button>
          </div>
        )}
      </Fragment>
    );
  }

  render() {
    if (!this.state.reviews) {
      return null;
    }

    const { moreInfoOpen, myReview, page, rawReviews, fetchingReviews } = this.state;
    const { description, isFormValid } = myReview;
    const reviewsCount = rawReviews.length;
    const thereIsMore =  reviewsCount > (page * this.reviewsPerPage);
    const readers = ['Boeken', 'Engelse Boeken', 'eBooks'];
    const userType = readers.includes(this.props.productCatalog) ? 'comp.reviews.user.readers' : 'comp.reviews.user.default';

    return (
      <div className="container" id="section-reviews">
        <div className="reviews-component" id="reviewsRef">
          <SectionHeader
            title={t('pdp.menu.panel.reviews')}
            subtitle={miniMustache({
              string: t('comp.reviews.subtitle'),
              replacements: { userType: t(userType) || 'lezers' },
            })}
          />
          <Row>
            <Col md={6}>
              <div className="reviews-summary-block">
                <Row>
                  <Col lg={6}>
                    <Rating rating={this.rating} size="large"/>
                    <span className="revieuws-summery-rating">{this.rating} <Text>comp.reviews.stars</Text> | {reviewsCount} <Text>comp.reviews.reviews</Text></span>
                  </Col>
                  <Col lg={6}>
                    { /* TODO: implement sorting */ }
                  </Col>
                </Row>
              </div>
              <div className="reviews-rows-container">
                {this.state.reviews.map((review, i) => {
                  const keyReview = review.id || `newreview-${i}`;
                  const dataTestId = `review-${i + 1}`;
                  return (
                    <Review key={keyReview} review={review} dataTestId={dataTestId}/>
                  );
                })}
              </div>
              {thereIsMore && (
                <div className={`reviews-more ${fetchingReviews ? 'disabled' : ''}`} onClick={this.extendReviews}>
                  <Icon name="ico-arrow-circle-down" size={100} />
                  <Text>comp.reviews.button.more</Text>
                </div>
              )}
            </Col>
            <Col md={6}>
              <div className="reviews-add-review-cta">
                <h3><Text>comp.reviews.add-rating.top</Text></h3>
                <div className="create-review-rating" data-test-id="create-review-rating">
                  <Rating size="large" rating={this.state.myReview.rating || 0} onClick={this.handleReviewChangeRating} hoverable/>
                </div>
                <span><Text>comp.reviews.add-rating.small-text</Text></span>
              </div>
              <div className="preview-anchor">
                <div ref={this.preview}/>
              </div>
              {this.state.writeModus === 0 && (
                <div className="reviews-rating-banner">
                  <Text>comp.reviews.add-rating.banner-text</Text>
                </div>
              )}
              {this.state.writeModus === 1 && (
                <div className="reviews-add-form-container">
                  <div>
                    <form
                      ref={this.form}
                      onSubmit={this.handleSubmit}
                      noValidate
                    >
                      <Text>comp.reviews.add-rating.title</Text>
                      <input
                        name="title"
                        className="form-control"
                        data-test-id="input-review-title"
                        value={this.state.myReview.title}
                        onChange={this.handleReviewChange('title')}
                        placeholder={t('comp.reviews.input.title.placeholder')}
                        autoComplete="off"
                        ref={this.title}
                      />
                      <div className={'reviews-validation'}>
                        <div className={'reviews-error-messsage'}>
                          {this.title.current && this.title.current.validationMessage}
                        </div>
                      </div>
                      <div className="reviews-add-rating-titles">
                        <div><Text>comp.reviews.add-rating.review-text</Text></div>
                        <div className="reviews-add-rating-tips-title" data-test-id="reviews-tips-title" onClick={this.toggleTips}>
                          <span className="reviews-add-rating-tips-text">
                            <Text>comp.reviews.add-rating.review-text-more-info</Text>
                          </span>
                          <Icon
                            name={moreInfoOpen ? 'ico-close-circle-filled' : 'ico-info-filled'}
                            size={100}
                          />
                        </div>
                      </div>
                      <div className={`reviews-add-rating-tips-panel ${moreInfoOpen ? 'visible' : ''}`} data-test-id="reviews-tips-panel">
                        <ol className="reviews-add-rating-tips-list" dangerouslySetInnerHTML={miniMustache({
                          string: t('comp.reviews.add-rating.more-info-text'),
                          html: true,
                        })} />
                      </div>
                      <textarea
                        name="description"
                        data-test-id="input-review-description"
                        value={this.state.myReview.description}
                        onChange={this.handleReviewChange('description')}
                        rows={8}
                        placeholder={t('comp.reviews.input.text.placeholder')}
                        minLength={this.descriptionMinLength}
                        ref={this.description}
                      />
                      <div className={'reviews-validation'}>
                        <div className={'reviews-error-messsage'}>{this.description.current && this.description.current.validationMessage}</div>
                        <div className={'reviews-description-char-counter'}>
                          {`${description.length} / ${this.descriptionMaxLength}`}
                        </div>
                      </div>
                      <div className="reviews-button-block">
                        <Button variant="secondary" size="large" onClick={this.gotoStep(0)}>
                          <Text>comp.reviews.button.cancel</Text>
                        </Button>
                        <Button
                          variant="primary"
                          size="large"
                          type="submit"
                          disabled={!isFormValid}
                          onClick={() => false}
                          data-test-id="button-review-submit"
                        >
                          <Text>comp.reviews.button.save</Text>
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {this.state.writeModus === 2 && this.renderReviewPreview}
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}
Reviews.propTypes = {
  productName: PropTypes.string,
  store: PropTypes.object,
  sku: PropTypes.string,
  rating: PropTypes.number,
  productCatalog: PropTypes.string,
  setReviewCount: PropTypes.func,
};


export default Reviews;
